import { useMemo } from 'react';
import { Target, TrendingUp, CheckCircle2, AlertTriangle, ArrowUpCircle, XCircle } from 'lucide-react';

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('/');
  if (parts.length === 2) return new Date(parseInt(parts[1]), parseInt(parts[0]) - 1, 1);
  if (parts.length === 3) return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  return null;
};

const getStatus = (roi) => {
  if (roi < 0) return { color: 'red', label: 'Negativo', icon: XCircle };
  if (roi < 4) return { color: 'orange', label: 'Por debajo del objetivo', icon: AlertTriangle };
  if (roi <= 6) return { color: 'green', label: 'En objetivo (4-6%)', icon: CheckCircle2 };
  return { color: 'blue', label: 'Por encima del objetivo', icon: ArrowUpCircle };
};

const getAnnualStatus = (roi) => {
  if (roi < 0) return { color: 'red', label: 'Negativo' };
  if (roi < 20) return { color: 'orange', label: 'Por debajo (objetivo 20-25%)' };
  if (roi <= 25) return { color: 'green', label: 'En objetivo (20-25%)' };
  return { color: 'blue', label: 'Por encima del objetivo' };
};

const colorClasses = {
  green: { border: 'border-green-500/50', bg: 'bg-green-500/10', text: 'text-green-400' },
  orange: { border: 'border-orange-500/50', bg: 'bg-orange-500/10', text: 'text-orange-400' },
  blue: { border: 'border-blue-500/50', bg: 'bg-blue-500/10', text: 'text-blue-400' },
  red: { border: 'border-red-500/50', bg: 'bg-red-500/10', text: 'text-red-400' },
  gray: { border: 'border-gray-700', bg: 'bg-gray-800/40', text: 'text-gray-500' },
};

const QuarterCard = ({ quarter, data }) => {
  if (!data) {
    return (
      <div className={`rounded-xl border ${colorClasses.gray.border} ${colorClasses.gray.bg} p-4`}>
        <p className="text-gray-400 text-sm font-bold mb-2">{quarter}</p>
        <p className="text-2xl font-bold text-gray-600">—</p>
        <p className="text-xs text-gray-600 mt-2">Sin trades cerrados</p>
      </div>
    );
  }
  const status = getStatus(data.roi);
  const c = colorClasses[status.color];
  const Icon = status.icon;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`} data-testid={`quarter-card-${quarter.replace(' ', '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-300 text-sm font-bold">{quarter}</p>
        <Icon size={16} className={c.text} />
      </div>
      <p className={`text-2xl font-bold ${c.text}`}>{data.roi >= 0 ? '+' : ''}{data.roi.toFixed(2)}%</p>
      <p className={`text-xs mt-1 ${c.text}`}>{status.label}</p>
      <p className="text-xs text-gray-500 mt-2">{data.trades} trade{data.trades !== 1 ? 's' : ''} cerrado{data.trades !== 1 ? 's' : ''}</p>
    </div>
  );
};

const QuarterlyPerformancePanel = ({ blockchainTrades, forexTrades }) => {
  const yearlyData = useMemo(() => {
    // Portfolio view: global trades only (no client_id), grouped by quarter of date_close
    const globalTrades = [...blockchainTrades, ...forexTrades].filter(t => !t.client_id);
    const years = {};
    globalTrades.forEach(t => {
      const closeDate = parseDate(t.date_close);
      if (!closeDate || isNaN(closeDate)) return;
      const year = closeDate.getFullYear();
      const quarter = Math.floor(closeDate.getMonth() / 3) + 1;
      const roi = t.risked_percent * t.rr_ratio;
      if (!years[year]) years[year] = { quarters: {}, total: 0, trades: 0 };
      if (!years[year].quarters[quarter]) years[year].quarters[quarter] = { roi: 0, trades: 0 };
      years[year].quarters[quarter].roi += roi;
      years[year].quarters[quarter].trades += 1;
      years[year].total += roi;
      years[year].trades += 1;
    });
    return Object.entries(years).sort((a, b) => b[0] - a[0]);
  }, [blockchainTrades, forexTrades]);

  return (
    <div className="space-y-6" data-testid="quarterly-performance-panel">
      {/* Explanation */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex items-start gap-3">
        <Target size={20} className="text-cyan-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-400">
          <p className="text-white font-bold mb-1">Objetivos de rentabilidad (vista Portfolio — solo trades globales)</p>
          <p>Cada trade aporta <span className="text-cyan-400 font-mono">Riesgo% × R:R</span> al trimestre en que se <b>cierra</b> (date_close). Objetivo: <span className="text-green-400 font-bold">4-6% por trimestre</span> → <span className="text-green-400 font-bold">20-25% anual</span>. Ajusta el Riesgo% o el R:R de tus trades hasta que los cartelitos se pongan verdes.</p>
        </div>
      </div>

      {yearlyData.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center text-gray-500">
          No hay trades globales con fecha de cierre válida
        </div>
      )}

      {yearlyData.map(([year, data]) => {
        const annualStatus = getAnnualStatus(data.total);
        const ac = colorClasses[annualStatus.color];
        return (
          <div key={year} className="bg-gray-900 border border-gray-800 rounded-lg p-5" data-testid={`year-panel-${year}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <TrendingUp size={18} className="text-cyan-400" /> {year}
              </h3>
              <div className={`flex items-center gap-2 rounded-lg border ${ac.border} ${ac.bg} px-4 py-2`} data-testid={`annual-badge-${year}`}>
                <span className="text-gray-400 text-xs">ANUAL</span>
                <span className={`text-xl font-bold ${ac.text}`}>{data.total >= 0 ? '+' : ''}{data.total.toFixed(2)}%</span>
                <span className={`text-xs ${ac.text}`}>{annualStatus.label}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(q => (
                <QuarterCard key={q} quarter={`Q${q} ${year}`} data={data.quarters[q]} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500/60"></span> En objetivo</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-orange-500/60"></span> Por debajo</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500/60"></span> Por encima</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500/60"></span> Negativo</span>
      </div>
    </div>
  );
};

export default QuarterlyPerformancePanel;
